import { useEffect, useRef } from "react";
import Lenis from "@studio-freight/lenis";

import { ScrollTrigger, gsap, prefersReducedMotion } from "./motion";

/**
 * Smooth scrolling, driven from GSAP's ticker so Lenis and ScrollTrigger
 * always agree on the scroll position (no jitter on pinned/scrubbed tweens).
 *
 * Returns a ref holding the Lenis instance, so callers can `scrollTo()` an
 * anchor without reaching for a global.
 */
export function useSmoothScroll() {
  const lenisRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;

    const lenis = new Lenis({
      duration: 1.05,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false, // native momentum feels better on phones
      touchMultiplier: 1.6,
    });

    lenisRef.current = lenis;

    lenis.on("scroll", ScrollTrigger.update);

    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(tick);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return lenisRef;
}

/** Smoothly move to an in-page target, respecting the sticky header height. */
export function scrollToSection(lenis, selector, offset = -80) {
  const target = document.querySelector(selector);
  if (!target) return;

  if (lenis) {
    lenis.scrollTo(target, { offset, duration: 1.2 });
  } else {
    const top = target.getBoundingClientRect().top + window.scrollY + offset;
    window.scrollTo({ top, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  }
}
